import type { NavigationService } from '@lib/services/navigation/navigation-service'

import { IS_BROWSER } from '@constants/globals'

type Experiments = { [key: string]: string }

/**
 * ExperimentOverrides handles parsing and managing experiment overrides from URL parameters and localStorage.
 *
 * This class provides:
 * - Parsing URL query parameters for experiment overrides
 * - Managing localStorage fallback for persisting overrides
 * - Handling reset functionality for clearing overrides
 */
export class ExperimentOverrides {
  private static readonly STORAGE_KEY = 'shExperimentOverrides'

  constructor(private readonly navigationService?: NavigationService) {}

  /**
   * Parse experiment string into experiment object
   * Format: "experimentName:treatmentName,experiment2:treatment2"
   */
  private parseToExperimentObj(experiments: string): Experiments {
    return experiments
      ? experiments.split(',').reduce((acc, val) => {
          const tuple = val.split(':')
          return tuple.length < 2 ? { ...acc } : { ...acc, [tuple[0]]: tuple[1] }
        }, {})
      : {}
  }

  /**
   * Get experiment overrides from localStorage as fallback
   */
  private getLocalStorageFallbackOverrides(): Experiments {
    if (!IS_BROWSER || !window.localStorage) {
      return {}
    }
    try {
      const localStorageEntry = window.localStorage.getItem(ExperimentOverrides.STORAGE_KEY)
      return localStorageEntry ? this.parseToExperimentObj(localStorageEntry) : {}
    } catch {
      // Ignore localStorage errors
      return {}
    }
  }

  /**
   * Remove experiment overrides from localStorage
   */
  private clearStoredOverrides(): void {
    if (!IS_BROWSER) return

    try {
      window.localStorage.removeItem(ExperimentOverrides.STORAGE_KEY)
    } catch {
      // Ignore localStorage errors
    }
  }

  /**
   * Store experiment overrides in localStorage
   */
  private storeOverrides(overrideString: string): void {
    if (!IS_BROWSER) return

    try {
      window.localStorage.setItem(ExperimentOverrides.STORAGE_KEY, overrideString)
    } catch {
      // Ignore localStorage errors
    }
  }

  /**
   * Get current experiment overrides from URL params or localStorage
   */
  getExperimentOverrides(): Experiments {
    let parsedExperimentOverrides = {}
    const queryParams = this.navigationService?.currentQueryParams || {}

    // Convert query values to strings for experiment override handling
    const experimentOverrideReset =
      typeof queryParams.experimentOverrideReset === 'string'
        ? queryParams.experimentOverrideReset
        : undefined
    const experimentOverrides =
      typeof queryParams.experimentOverrides === 'string'
        ? queryParams.experimentOverrides
        : undefined

    // Handle reset functionality
    if (experimentOverrideReset) {
      this.clearStoredOverrides()
    }

    try {
      // If no URL overrides, fall back to localStorage
      if (!experimentOverrides) {
        return this.getLocalStorageFallbackOverrides()
      }

      // Parse and store URL overrides
      if (experimentOverrides) {
        parsedExperimentOverrides = this.parseToExperimentObj(experimentOverrides)
        this.storeOverrides(experimentOverrides)
      }

      return parsedExperimentOverrides
    } catch (error) {
      console.error('Error with experiment override setting. Please check your params.', error)
    }
    return parsedExperimentOverrides
  }

  /**
   * Check if there are any active experiment overrides
   */
  hasOverrides(): boolean {
    const overrides = this.getExperimentOverrides()
    return Object.keys(overrides).length > 0
  }

  /**
   * Clear all experiment overrides
   */
  clearOverrides(): void {
    this.clearStoredOverrides()
  }
}
